/*
 * NAO-97 product page contract: window.NaomiPDP (BUILD-PLAN 1.5).
 * Loaded by sections/main-product.liquid when its naomi_pdp setting is on, before
 * any unit script. The Liquid half of the contract is at the top of
 * sections/naomi-pdp-stack.liquid.
 *
 * JS API
 *   NaomiPDP.state        { variantId, options: { [role]: value }, price, available, sizePending }
 *                         price is in cents (Shopify variant.price). sizePending is true while a
 *                         ring PDP has no size or sizer picked yet (the size-dropdown placeholder).
 *   NaomiPDP.on(fn)       fn(state) now, then after every Dawn PUB_SUB_EVENTS.variantChange and
 *                         every ring sizer change (nj:sizer). Returns an unsubscribe function.
 *   NaomiPDP.select(role, value)
 *                         Checks the buy box input for that role and value and fires its change,
 *                         so Dawn's VariantSelects stays the only variant state. Stack controls
 *                         (chain radios) never hold state of their own. Returns
 *                         false when no such input exists.
 *   NaomiPDP.role(name)   karat | colour | ring_size | length | stone_size | pearl_size |
 *                         post_length | null, for the EN and KO option names. Same table as
 *                         snippets/pdp-option-role.liquid (tests/naomi-pdp-role.test.js).
 *   NaomiPDP.roles(name)  the same, as a list, with the compound "Gold Material & <name>"
 *                         read as ['karat', <role>] (the snippet's parts mode); state.options
 *                         then carries both halves of the value ("14K Gold", "1ct").
 *   NaomiPDP.track(event, props)
 *                         Calls window.NaomiPDPTrack(event, props) (T12) when present, else a
 *                         no-op. The common properties (handle, store, template,
 *                         mobile_cta, offers_position, variant_id, viewport) are added here.
 *   NaomiPDP.fade(el)     the option-change dissolve (.pdp-fade, section-main-product.css) on a node
 *                         whose content just changed. A no-op until the shopper first picks an
 *                         option, so nothing fades on load, and under reduced motion.
 *   NaomiPDP.settings     the main-product 1.10 toggles: mobile_cta,
 *                         desktop_first_screen, offers_position, show_installments, gift_option,
 *                         client_care_line. Also mirrored on <html> as data-pdp-<name> for CSS
 *                         (underscores become dashes: data-pdp-offers-position="below_button").
 *   NaomiPDP.product      { id, handle, store: 'global' | 'kr', options: [option names] }
 *
 * DOM hooks
 *   [data-pdp-buy]        buy box root (#ProductInfo-<section>)
 *   [data-pdp-config]     configuration line, text only (T3 writes it, pdp-bar mirrors it)
 *   [data-pdp-cta]        the main Add to bag button
 *   [data-pdp-chapter]    chapter wrappers (details | size | materials | delivery), data-label
 *   [data-pdp-variant]    space- or comma-separated variant ids; hidden unless one is selected
 *   [data-pdp-variant-on] same ids; gets class is-on while one is selected (stays visible)
 *   [data-pdp-not-carat]  carat tags ("3ct", or "1ct 3ct"); hidden while the selected variant's
 *                         options carry one of them. On an element that also has
 *                         [data-pdp-variant] both apply: shown only when neither hides it
 *   [data-pdp-carat]      carat tags ("3ct", or "1ct 2ct"); hidden unless the selected variant's
 *                         options carry one of them
 *   [data-pdp-karat]      "14K" or "18K"; hidden unless it matches the selected karat
 *   #pdp-size-guide #pdp-length-guide #pdp-ladder #pdp-arrival #pdp-warranty   content anchors
 */
(() => {
  // Option name (trimmed, lowercased) to role. Compound names are absent on purpose: null.
  const ROLES = {
    'gold material': 'karat',
    '골드 소재': 'karat',
    color: 'colour',
    colors: 'colour',
    colour: 'colour',
    'pendant color': 'colour',
    'pendant colors': 'colour',
    'color (front & back)': 'colour',
    컬러: 'colour',
    '펜던트 컬러': 'colour',
    '컬러 (앞면/뒷면)': 'colour',
    'ring size': 'ring_size',
    'ring size (us)': 'ring_size',
    '링 사이즈': 'ring_size',
    '반지 사이즈': 'ring_size',
    'chain length': 'length',
    'chain length (including 2cm extender)': 'length',
    length: 'length',
    '체인 길이': 'length',
    '체인 길이 (2cm 연장 체인 포함)': 'length',
    '체인 길이 (연장 체인 2cm 포함)': 'length',
    길이: 'length',
    'diamond size': 'stone_size',
    'diamond size (main stone)': 'stone_size',
    '다이아몬드 크기': 'stone_size',
    '다이아몬드 크기 (메인 스톤)': 'stone_size',
    'pearl size': 'pearl_size',
    '진주 크기': 'pearl_size',
    'post length': 'post_length',
    '포스트 길이': 'post_length',
    '침 길이': 'post_length',
  };
  const role = (name) => ROLES[String(name ?? '').trim().toLowerCase()] ?? null;
  // parts mode of snippets/pdp-option-role.liquid: "Gold Material & Diamond Size" is
  // ['karat', 'stone_size'], its values split at the first ", " ("14K Gold, 1ct").
  // Any other name is [role] or [].
  // KR: "골드 소재 및 다이아몬드 크기" ("14K, 1ct").
  const COMPOUND = ['gold material & ', '골드 소재 및 '];
  const roles = (name) => {
    const n = String(name ?? '').trim().toLowerCase();
    const head = COMPOUND.find((c) => n.startsWith(c));
    if (head) {
      const rest = role(n.slice(head.length));
      return rest && rest !== 'karat' ? ['karat', rest] : [];
    }
    const r = role(n);
    return r ? [r] : [];
  };
  const valueParts = (value, count) => {
    const v = String(value ?? '');
    if (count < 2) return [v];
    const i = v.indexOf(', ');
    return i < 0 ? [v] : [v.slice(0, i), v.slice(i + 2)];
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = { ROLES, role, roles, valueParts };
  if (typeof window === 'undefined' || window.NaomiPDP) return;

  const productEl = document.querySelector('script[data-pdp-product]');
  const product = productEl ? JSON.parse(productEl.textContent) : { handle: '', store: 'global', options: [], settings: {} };
  const settings = product.settings || {};
  delete product.settings;
  const listeners = new Set();

  Object.entries(settings).forEach(([key, value]) => {
    document.documentElement.setAttribute(`data-pdp-${key.replace(/_/g, '-')}`, String(value));
  });

  const buyRoot = () => document.querySelector('[data-pdp-buy]');
  // product-info keeps the old <variant-selects> hidden for 500ms after a swap.
  const variantSelects = () =>
    Array.from(buyRoot()?.querySelectorAll('variant-selects') || []).find((el) => el.style.display !== 'none');
  const sizePending = () => !!buyRoot()?.querySelector('.nj-size-dropdown__current--placeholder');
  const karatOf = (value) => (String(value ?? '').match(/\d+K/i) || [''])[0].toUpperCase();

  const readVariant = () => {
    const json = variantSelects()?.querySelector('[data-selected-variant]')?.textContent;
    try {
      return json ? JSON.parse(json) : null;
    } catch {
      return null;
    }
  };

  const state = { variantId: null, options: {}, price: null, available: false, sizePending: false, carats: [] };

  const setState = (variant) => {
    state.variantId = variant?.id ?? null;
    state.price = variant?.price ?? null;
    state.available = !!variant?.available;
    // carat tags of the option values, as snippets/product-carat-tags.liquid reads them
    state.carats = (variant?.options || []).flatMap((value) =>
      String(value)
        .toLowerCase()
        .split(/[|,&\/·]/)
        .map((token) => token.replace(/\s/g, ''))
        .filter((token) => /^[\d.]*ct$/.test(token) && /\d/.test(token))
    );
    state.options = {};
    (variant?.options || []).forEach((value, i) => {
      const rs = roles(product.options[i]);
      const parts = valueParts(value, rs.length);
      rs.forEach((r, j) => {
        if (parts[j] != null) state.options[r] = parts[j];
      });
    });
    state.sizePending = sizePending();
  };

  const applyHooks = () => {
    // a single-variant product has no picker, so no variant id: keep the server render
    if (state.variantId != null) {
      const id = String(state.variantId);
      document.querySelectorAll('[data-pdp-variant]').forEach((el) => {
        el.hidden = !el.dataset.pdpVariant.split(/[\s,]+/).includes(id);
      });
      document.querySelectorAll('[data-pdp-variant-on]').forEach((el) => {
        const on = el.dataset.pdpVariantOn.split(/[\s,]+/).includes(id);
        el.classList.toggle('is-on', on);
        if (on) el.setAttribute('aria-current', 'true');
        else el.removeAttribute('aria-current');
      });
    }
    if (state.variantId != null) {
      // after the [data-pdp-variant] pass, so an element with both stays hidden by either
      document.querySelectorAll('[data-pdp-not-carat]').forEach((el) => {
        const off = el.dataset.pdpNotCarat.split(/[\s,]+/).some((tag) => state.carats.includes(tag));
        el.hidden = off || ('pdpVariant' in el.dataset && el.hidden);
      });
      // one tag ("3ct") or several ("1ct 2ct", a pdp-spec carat row): shown when any matches
      document.querySelectorAll('[data-pdp-carat]').forEach((el) => {
        el.hidden = !el.dataset.pdpCarat.split(/[\s,]+/).some((tag) => state.carats.includes(tag));
      });
    }
    const karat = karatOf(state.options.karat);
    document.querySelectorAll('[data-pdp-karat]').forEach((el) => {
      el.hidden = !!karat && karatOf(el.dataset.pdpKarat) !== karat;
    });
  };

  const notify = () => {
    applyHooks();
    listeners.forEach((fn) => {
      try {
        fn(state);
      } catch (error) {
        console.error(error);
      }
    });
  };

  const inputFor = (r, value) => {
    const root = variantSelects();
    if (!root) return null;
    const radio = Array.from(root.querySelectorAll('input[type="radio"]')).find(
      // the Liquid-captured name ends in a newline: "Color-2\n"
      (input) => input.value === value && role(input.name.trim().replace(/-\d+$/, '')) === r
    );
    if (radio) return radio;
    const select = Array.from(root.querySelectorAll('select[name^="options["]')).find(
      (el) => role(el.name.slice(8, -1)) === r
    );
    return select && Array.from(select.options).some((o) => o.value === value) ? select : null;
  };

  // armed by the first option pick (VariantSelects), so the page's own first renders stay still
  let armed = false;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  ['animationend', 'animationcancel'].forEach((type) =>
    document.addEventListener(type, ({ target, animationName }) => {
      if (animationName === 'pdp-fade') target.classList.remove('pdp-fade');
    })
  );

  const viewport = () => (window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop');

  window.NaomiPDP = {
    state,
    product,
    settings,
    role,
    roles,
    on(fn) {
      listeners.add(fn);
      fn(state);
      return () => listeners.delete(fn);
    },
    select(r, value) {
      const input = inputFor(r, value);
      if (!input) return false;
      if (input.tagName === 'SELECT') {
        if (input.value === value) return true;
        input.value = value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (!input.checked || r === 'ring_size') {
        // click, not a bare change: size-dropdown.js counts a click in the ring list as a choice
        input.click();
      }
      return true;
    },
    fade(el) {
      // a node not drawn (display none) never ends the animation, so it would fade on showing
      if (!el || !armed || reduce.matches || !el.getClientRects().length) return;
      // off and on again, with a style read between, so a change mid-fade replays it
      el.classList.remove('pdp-fade');
      void el.offsetWidth;
      el.classList.add('pdp-fade');
    },
    track(event, props = {}) {
      if (typeof window.NaomiPDPTrack !== 'function') return;
      window.NaomiPDPTrack(event, {
        handle: product.handle,
        store: product.store,
        template: 'naomi',
        mobile_cta: settings.mobile_cta,
        offers_position: settings.offers_position,
        variant_id: state.variantId,
        viewport: viewport(),
        ...props,
      });
    },
  };

  setState(readVariant());
  applyHooks();

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, () => (armed = true));
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      setState(data?.variant ?? readVariant());
      notify();
    });
  }
  document.addEventListener('nj:sizer', () => {
    state.sizePending = sizePending();
    notify();
  });

  // Shop Pay line in the page's face: Shopify's shadow DOM forces its own font and a bold
  // amount, which read louder than the shipping line above it. The logo stays Shopify's.
  const buy = document.querySelector('[data-pdp-buy]');
  if (buy && 'adoptedStyleSheets' in Document.prototype) {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(
      '#shopify-installments, #shopify-installments * { font-family: inherit !important; }' +
        ' .font-bold { font-weight: 500 !important; color: var(--n-ink, #111110); }'
    );
    const quiet = () =>
      buy.querySelectorAll('shopify-payment-terms').forEach(({ shadowRoot: root }) => {
        if (root && !root.adoptedStyleSheets.includes(sheet)) root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
      });
    customElements.whenDefined('shopify-payment-terms').then(() => {
      quiet();
      new MutationObserver(quiet).observe(buy, { childList: true, subtree: true });
    });
  }
})();
